import { MouseEventHandler, useContext } from "react";
import { useListItem } from "@floating-ui/react";
import { CheckIcon } from "../../materials/icons/check-icon/CheckIcon";
import { Body } from "../body/Body";
import { Checkbox } from "../checkbox/Checkbox";
import { ItemContext } from "../list-navigation/ListNavigation.Parts";
import { IconWrapper, LabelWrapper, Props, Wrapper } from "./ListItem.Parts";

export const ListItem = ({
  label,
  Icon,
  RightIcon,
  disabled,
  children,
  checkbox,
  selected,
  isActive,
  isFocusVisible,
  checkboxProps = {},
  labelVariant = "normal",
  Checkbox: CustomCheckbox,
  ref,
  ...rest
}: Props) => {
  const hasCheckbox = checkbox || CustomCheckbox;

  const navContext = useContext(ItemContext);

  // useListItem always runs (hooks can't be conditional) — when there is no
  // FloatingList ancestor the default context returns index: -1 and a no-op ref.
  const { ref: listItemRef, index } = useListItem({ label });

  // An item is "managed" once FloatingList has resolved its DOM position (index
  // becomes a non-negative integer after the initial mount cycle).
  const isManaged = navContext !== null && index !== -1;

  // Pass existing props into getItemProps so floating-ui composes its handlers
  // (e.g. onKeyDown, onClick) with the item's own handlers rather than replacing them.
  const wrapperProps = isManaged
    ? navContext.getItemProps({
        ...rest,
        role: "option",
        tabIndex: navContext.activeIndex === index ? 0 : -1,
      })
    : rest;

  // listItemRef must always be called so FloatingList can register the DOM node
  // on mount — skipping it on first render (when index is still -1) would break
  // registration and leave the item permanently unmanaged.
  const mergedRef = (node: HTMLDivElement | null) => {
    listItemRef(node);
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  return (
    <Wrapper
      {...wrapperProps}
      ref={mergedRef}
      $disabled={disabled}
      aria-disabled={disabled}
      $isFocusVisible={isFocusVisible}
      $isActive={isManaged ? navContext.activeIndex === index : isActive}
      onClick={
        disabled
          ? undefined
          : (wrapperProps.onClick as MouseEventHandler<HTMLDivElement>)
      }
    >
      {/* Checkbox variant with CustomCheckbox */}
      {!Icon && CustomCheckbox}

      {/* Checkbox variant with default Checkbox */}
      {!Icon && !CustomCheckbox && checkbox && (
        <Checkbox
          tabIndex={-1}
          disabled={disabled}
          variant={selected ? "checked" : "unchecked"}
          {...checkboxProps}
        />
      )}

      {/* Icon variant */}
      {!hasCheckbox && Icon && <IconWrapper>{Icon}</IconWrapper>}

      {/* Default label if no children provided */}
      <LabelWrapper>
        {children ? children : <Body variant={labelVariant}>{label}</Body>}
      </LabelWrapper>

      {/* Selected option */}
      {!hasCheckbox && selected && (
        <IconWrapper>
          <CheckIcon small />
        </IconWrapper>
      )}

      {/* Right-aligned trailing icon */}
      {!hasCheckbox && !selected && RightIcon && (
        <IconWrapper>{RightIcon}</IconWrapper>
      )}
    </Wrapper>
  );
};
